import { createElement, useMemo } from 'react';

import { type SidebarItem } from '@/components/Sidebar/Sidebar';
import { useT } from '@/core/providers/I18nProvider';
import { usePermissions } from '@/hooks/usePermissions';

import { EVERY_ROLE_SURFACE } from './sidebar-definition.types';
import { GROUP_LABEL_KEY, SIDEBAR_DEFINITIONS } from './sidebar-definitions';
import { useWiredLabsFlags } from './use-wired-labs-flags';

/**
 * Builds the localized, gated `SidebarItem[]` the AppShell renders from the
 * static `SIDEBAR_DEFINITIONS`. Extracted from `AppShell.tsx` (A23 file-size
 * discipline) so the shell component stays under the ~300-line ceiling.
 *
 * Independent gates compose (an item shows only if it passes every one):
 *   - Generic Labs gate — an entry carrying an `experimentalFlag` stays hidden
 *     until that flag resolves ON. ONE entry uses it today: `/experience`
 *     (Adaptive Compositions D4). The record comes from `useWiredLabsFlags`,
 *     shared with the settings search so a setting is never offered for a page
 *     this row hides. Fail-closed — an entry naming a flag absent from the
 *     record stays hidden, which is exactly how the former `/site-manual` row
 *     shipped DEAD (declared flag, no record entry — nothing rendered it,
 *     nothing failed).
 *
 *     Main's note called that coupling "the mechanism's one sharp edge… an
 *     `experimentalFlag` with no matching record entry hides its row FOREVER,
 *     and looks exactly like a routing bug". It is no longer sharp, and that
 *     is what this merge keeps: `experimentalFlag` is narrowed to
 *     `WiredSidebarLabsFlag` and the record below is EXACT over it, so a
 *     declared-but-unwired flag AND a wired-but-unresolved one are both
 *     compile errors rather than a silent hidden row. Tests could only ever
 *     pin the rows that exist; the type covers the next one.
 *
 *     There is no longer an INTERNAL surface gate beside it. Four operator-only
 *     flags once hid a row until a deploy flipped them; `/red-team` (ADR-0532),
 *     `/catalog-review` (ADR-0534) and `/compliance` (ADR-0536) each left with
 *     its page, and `/impact` — the last — was retired into the Analytics hub,
 *     which reports a killed `AGENT_IMPACT_ENABLED` on the DATA (`gateReason:
 *     FLAG_OFF`) rather than by withholding a page. The whole mechanism went
 *     with it (ADR-0330 superseded): the query, its resolver, the hook, the
 *     route wrapper and this field. A future operator-only surface brings it
 *     back with its first consumer.
 *
 * A GROUP is derived from the items that claim it, so a group whose every child
 * is filtered out here never reaches `Sidebar` and renders no heading.
 *   - RBAC surface gate — an entry carrying a `surface` is hidden from a role
 *     that lacks `view` on it, so a viewer/editor never lands on a Billing /
 *     API-keys / Team / Limits page they can't use. Fail-SAFE (the opposite of
 *     the Labs gate): the row shows while the role loads and only hides once the
 *     role is KNOWN to be below the bar, so a row is never hidden from a user who
 *     can reach it (`usePermissions().can` returns true while unknown).
 *
 * NO row carries a count pill today. `SidebarItem` still supports one (SYN-119
 * built it generically), but its only source was the pending-handoff queue,
 * which left with the handoff surface (ADR-0575). The next cheap, real,
 * tenant-scoped count wires here.
 */
export function useSidebarItems(): readonly SidebarItem[] {
  const { t } = useT();
  const { can } = usePermissions();
  // One hook per Labs-gated row (hooks cannot loop), resolved in one shared
  // place so the settings search gates on the same answer this row does.
  // Fail-closed: false while loading, on error, and for an id the server does
  // not list.
  const flagEnabled = useWiredLabsFlags();
  return useMemo<readonly SidebarItem[]>(() => {
    return SIDEBAR_DEFINITIONS.filter(
      (definition) =>
        // Generic Labs-gate: an entry naming an experimentalFlag stays hidden
        // until that flag resolves ON. No `=== true` guard against a missing
        // record entry any more — with the record EXACT over the flag union, a
        // lookup here cannot be `undefined`, and the compiler now proves what
        // that guard used to defend against at runtime (and silently).
        (definition.experimentalFlag === undefined || flagEnabled[definition.experimentalFlag]) &&
        // RBAC surface gate: hide a row from a role that can't view its
        // surface. Fail-safe — `can` returns true while the role loads, so a row
        // is never hidden from a user who can reach it.
        //
        // No `=== undefined` escape any more. `surface` is REQUIRED on the
        // definition (D-9 / ADR-0549), and a row that genuinely belongs to every
        // role says so with `EVERY_ROLE_SURFACE` rather than by omitting the
        // key. While the omission was the way to say it, it admitted THIRTEEN
        // rows nobody meant to admit, which is how an Analyst came to see the
        // whole agent product. A permissive fallback in a filter whose job is to
        // subtract is the wrong direction to fail in; the compiler now makes the
        // permissive answer something you have to type.
        (definition.surface === EVERY_ROLE_SURFACE || can(definition.surface, 'view')),
    ).map((definition) => ({
      to: definition.to,
      label: t(definition.labelKey),
      icon: createElement(definition.icon, { className: 'h-4 w-4' }),
      ...(definition.group !== undefined ? { group: t(GROUP_LABEL_KEY[definition.group]) } : {}),
    }));
  }, [t, can, flagEnabled]);
}
